import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Button, Icon, styles, Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { PriceDetails } from './components/Item/components';
import { getLabelTaxes, isValidCart } from './helpers';
import { MAX_FOOTER_ITEMS } from './Rates.constants';
import { L10N } from './Rates.l10n';
import * as style from './Rates.module.css';
import { debounce } from '../../helpers';
import { EVENT, ICON } from '../helpers';

const Footer = ({ cart = [], dataSource, extras, multiRoom, occupation = [], onSubmit = () => {}, ...others }) => {
  const { taxesType } = dataSource || {};
  const { currencyFormat, translate } = useLocale();
  const { value: { currency } = {} } = useStore();
  const refContainer = useRef();

  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const items = cart.filter((item) => item !== undefined);

    Event.publish(EVENT.MODAL_VISIBLE, { displayName: Footer.displayName, visible: items.length > 0 });

    const content = refContainer?.current;
    if (!content || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(
      debounce(() =>
        Event.publish(EVENT.FOOTER_HEIGHT_CHANGE, { height: items.length ? content.offsetHeight : undefined }),
      ),
    );
    observer.observe(content);

    const callback = ({ displayName }) => {
      if (displayName !== Footer.displayName) {
        visible && Event.publish(EVENT.MODAL_VISIBLE, { displayName: Footer.displayName, visible: items.length > 0 });
      }
    };

    Event.subscribe(EVENT.MODAL_VISIBLE, callback);

    return () => {
      Event.unsubscribe(EVENT.MODAL_VISIBLE, callback);
      observer.unobserve(content);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  useEffect(() => {
    !extras && setBusy(false);
  }, [extras]);

  const items = cart.filter((item) => item !== undefined);
  const totalPrice = cart.reduce((value, { price = 0 } = {}) => value + price, 0);
  const valid = !!dataSource && cart.length === occupation.length && isValidCart(cart);

  const handleSubmit = () => {
    setBusy(true);
    onSubmit();
  };

  const visible = items.length > 0;
  const currencyFormatProps = { currency, maximumFractionDigits: 0, minimumFractionDigits: 0 };
  const { testId } = others;

  return (
    <View
      {...others}
      ref={refContainer}
      className={styles(style.footer, visible && style.visible, others.className)}
      aria-hidden={visible ? 'false' : 'true'}
    >
      {items.length >= MAX_FOOTER_ITEMS && (
        <View className={style.expander}>
          <Button transparent onPress={() => setExpanded(!expanded)} testId={testId} className={style.button}>
            <Icon value={expanded ? ICON.EXPAND_MORE : ICON.EXPAND_LESS} />
            {translate(expanded ? L10N.LABEL_HIDE : L10N.LABEL_SHOW)}
          </Button>
        </View>
      )}
      <View>
        <View className={style.wrapper} wide>
          {items
            .filter(() => expanded)
            .map(({ amount = 1, id, name: boardName, price, roomName } = {}, index) => {
              const guests = occupation[index].reduce((total, { amount } = {}) => total + amount, 0);

              return (
                <View row key={`${id}${index}`} className={style.cartItem}>
                  {multiRoom && (
                    <View row className={style.room}>
                      <Icon value={guests === 1 ? ICON.PERSON : guests === 2 ? ICON.GROUP : ICON.GROUPS} />
                      <Text small>{guests}</Text>
                    </View>
                  )}

                  <View className={style.description}>
                    <Text bold action className={style.description}>
                      {`${roomName} `}
                      {multiRoom && <Text light small>{` | ${boardName}`}</Text>}
                    </Text>
                    {!multiRoom && (
                      <Text light small>
                        {boardName}
                      </Text>
                    )}
                  </View>

                  <Text bold action>
                    {currencyFormat({ ...currencyFormatProps, value: amount * price })}
                  </Text>

                  {!multiRoom && (
                    <Button busy={busy} disabled={!valid} large onPress={handleSubmit}>
                      {translate(L10N.ACTION_RESERVE)}
                    </Button>
                  )}
                </View>
              );
            })}
        </View>
      </View>

      {multiRoom && (
        <View row className={[style.wrapper, style.summary]}>
          <View className={style.totalPrice}>
            <Text bold headline level={2}>
              {currencyFormat({ ...currencyFormatProps, value: totalPrice })}
            </Text>

            {!!taxesType && (
              <Text light small>
                {translate(getLabelTaxes(taxesType))}
              </Text>
            )}

            <PriceDetails cart={cart} taxesType={taxesType} />
          </View>

          <Button
            busy={busy}
            disabled={!valid}
            large
            onPress={handleSubmit}
            aria-label={translate(L10N.ACTION_RESERVE)}
            testId={testId ? `${testId}-reserve` : undefined}
          >
            {translate(L10N.ACTION_RESERVE)}
          </Button>
        </View>
      )}
    </View>
  );
};

Footer.displayName = 'Mirai:Core:Rates:Footer';

Footer.propTypes = {
  cart: PropTypes.arrayOf(PropTypes.shape({})),
  dataSource: PropTypes.shape({}),
  extras: PropTypes.arrayOf(PropTypes.shape({})),
  multiRoom: PropTypes.bool,
  occupation: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        ages: PropTypes.arrayOf(PropTypes.number),
        amount: PropTypes.number,
        type: PropTypes.number,
      }),
    ),
  ),
  onSubmit: PropTypes.func,
};

export { Footer };
